import { getCachedDocsPage, getDocsLogicalPaths, getDocsPageIndex, stripDocFilenameOrderPrefix } from './docs-manifest';

export interface DocsNavNode {
  segment: string;
  /** Logical path for routing, e.g. en/table-order/configuration */
  fullPath: string;
  children: DocsNavNode[];
}

/** Manual order for top-level sections under each locale (sidebar). */
const TOP_LEVEL_SECTION_ORDER: Record<string, number> = {
  'organization-management': 1,
  'pos-setup': 2,
  'handy-pos': 3,
  'table-order': 4,
  report: 5,
};

export function formatDocsNavLabel(segment: string): string {
  const displaySegment = stripDocFilenameOrderPrefix(segment).slug;
  if (displaySegment.length === 2 && displaySegment === displaySegment.toLowerCase()) {
    return displaySegment.toUpperCase();
  }
  return displaySegment
    .split('-')
    .map(w => (w ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : w))
    .join(' ');
}

/** First Markdown ATX H1 in raw source (leading `# ` line). */
export function extractDocsTitleFromRaw(raw: string): string {
  const line = raw.match(/^#\s+(.+)$/m);
  return line ? line[1].trim() : '';
}

/**
 * Side nav / breadcrumb label: prefer the doc's H1 title so translated locales show native titles.
 * Falls back to slug-style formatting from the path's last segment.
 */
export function getDocsNavTitle(fullPath: string): string {
  const page = getCachedDocsPage(fullPath);
  if (page?.raw) {
    const title = extractDocsTitleFromRaw(page.raw);
    if (title) {
      return title;
    }
  }
  const seg = fullPath.includes('/') ? fullPath.slice(fullPath.lastIndexOf('/') + 1) : fullPath;
  return formatDocsNavLabel(seg);
}

/** Nested nav for one locale (first path segment), sorted by segment. */
export function buildDocsNavTree(locale: string): DocsNavNode {
  const paths = getDocsLogicalPaths().filter(p => p === locale || p.startsWith(`${locale}/`));

  const root: DocsNavNode = {
    segment: locale,
    fullPath: locale,
    children: [],
  };

  for (const path of paths) {
    const segments = path.split('/').filter(Boolean);
    if (segments[0] !== locale) {
      continue;
    }

    let node = root;
    const accParts = [locale];

    for (let i = 1; i < segments.length; i++) {
      accParts.push(segments[i]);
      const fullPath = accParts.join('/');
      const seg = segments[i];
      let child = node.children.find(c => c.segment === seg);
      if (!child) {
        child = { segment: seg, fullPath, children: [] };
        node.children.push(child);
      }
      node = child;
    }
  }

  const sortTree = (n: DocsNavNode, isLocaleRoot = false) => {
    n.children.sort((a, b) => {
      if (isLocaleRoot) {
        const orderA = TOP_LEVEL_SECTION_ORDER[a.segment];
        const orderB = TOP_LEVEL_SECTION_ORDER[b.segment];
        if (orderA != null && orderB != null && orderA !== orderB) {
          return orderA - orderB;
        }
        if (orderA != null && orderB == null) {
          return -1;
        }
        if (orderA == null && orderB != null) {
          return 1;
        }
      }
      const orderA = getDocsPageIndex(a.fullPath)?.docOrder;
      const orderB = getDocsPageIndex(b.fullPath)?.docOrder;
      const hasA = orderA != null;
      const hasB = orderB != null;
      if (hasA && hasB && orderA !== orderB) {
        return orderA - orderB;
      }
      if (hasA && !hasB) {
        return -1;
      }
      if (!hasA && hasB) {
        return 1;
      }
      return a.segment.localeCompare(b.segment);
    });
    n.children.forEach(c => sortTree(c, false));
  };
  sortTree(root, true);

  return root;
}
